<template>
    <Modal :title="$t('session.validation.explanation.title')" :is-open="isOpen" @update:is-open="setOpen($event)">
        <div v-if="loadFailed" class="bg-brand-red flex rounded-lg p-3"> {{ $t('error.failed-to-load') }} </div>

        <Loading v-else-if="proposal === undefined || metas === undefined" />

        <div
            v-else
            :data-interactive-tutorial="advancesTutorial ? 'session-validation' : undefined"
            class="flex flex-col space-y-3"
        >
            <div
                v-if="sessionSummary.date || sessionSummary.duration"
                class="text-text-darker flex flex-wrap items-baseline gap-x-2 text-xs leading-5 sm:text-sm"
            >
                <span v-if="sessionSummary.date">{{ sessionSummary.date }}</span>
                <span v-if="sessionSummary.date && sessionSummary.duration" aria-hidden="true">·</span>
                <span v-if="sessionSummary.duration" class="font-medium">{{ sessionSummary.duration }}</span>
            </div>

            <!-- Only about the values found automatically: not shown once the rider edits them -->
            <Information v-if="!editing">
                {{ $t('session.validation.explanation.text') }}
            </Information>

            <template v-if="!editing">
                <div class="bg-dark-light divide-dark-lighter flex flex-col divide-y rounded-lg px-3">
                    <div v-for="line in lines" :key="line.key" class="flex min-w-0 items-center gap-3 py-2.5">
                        <img
                            v-if="line.value && line.photo"
                            class="h-8 w-8 shrink-0 rounded-full object-cover"
                            :src="line.photo"
                            alt=""
                        />
                        <span
                            v-else-if="line.value && line.initials"
                            class="bg-brand-base/24 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold tracking-[0.4px] text-[#b8bfff]"
                        >
                            {{ line.initials }}
                        </span>
                        <!-- A dashed circle for what was not found. -->
                        <span
                            v-else
                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                            :class="
                                line.value
                                    ? 'bg-dark-lighter text-text-darker'
                                    : 'text-text-gray border-[1.5px] border-dashed border-[#5a5c62]'
                            "
                        >
                            <component :is="line.icon" v-if="line.icon" class="h-4 w-4" stroke-width="2" />
                        </span>

                        <span class="flex min-w-0 flex-1 flex-col">
                            <span class="text-text-darker text-xxs leading-3.5 font-bold tracking-widest uppercase">
                                {{ line.label }}
                            </span>
                            <span class="truncate text-sm" :class="{ 'text-text-gray': !line.value }">
                                {{ line.value || $t('validations.not-filled-in') }}
                            </span>
                        </span>

                        <span
                            v-if="line.origin"
                            class="text-text-darker inline-flex shrink-0 items-center gap-1.25 rounded-full bg-white/7 px-2 py-0.75 text-xs"
                        >
                            <component :is="line.origin.icon" class="h-3 w-3" stroke-width="2.2" />
                            {{ line.origin.text }}
                        </span>
                    </div>
                </div>

                <Button :action="confirm" :enabled="!saving">{{ $t('session.validation.confirm') }}</Button>
                <Button secondary :action="() => (editing = true)" :enabled="!saving">
                    {{ $t('form.edit') }}
                </Button>
            </template>

            <template v-else>
                <SessionMetadataFields
                    v-model:horse-id="form.horseId"
                    v-model:rider-id="form.riderId"
                    v-model:discipline="form.discipline"
                    :horses="horsesOptions || []"
                    :riders="ridersOptions || []"
                    :discipline-options="disciplineOptions"
                    :empty-option-label="$t('form.none')"
                />

                <HeartrateUploadSelect v-model="form.heartrateUploadId" :uploads="heartrateOptions" />

                <div class="rounded-md pt-1 shadow-2xs">
                    <Button :action="saveForm" :enabled="!saving"> {{ $t('form.save') }} </Button>
                </div>
            </template>
        </div>
    </Modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'
import { ClockIcon, FlagIcon, HeartIcon, SignalIcon, UserIcon } from '@heroicons/vue/24/outline'

import fetcher from '@/lib/API/fetcher'
import { initialsOf, photoOf } from '@/lib/helpers/avatar'
import useHorses from '@/lib/hooks/use-horses'
import { activeHorsesOf, withKeptHorse } from '@/lib/horses/archived'
import useSessions from '@/lib/hooks/use-sessions'
import { reloadEvents } from '@/lib/hooks/use-events'
import useSensors from '@/lib/hooks/use-sensors'
import useRiders from '@/lib/hooks/use-riders'
import useMetas from '@/lib/hooks/use-metas'
import { secondsToHMS } from '@/lib/helpers/convert'
import { parseNullableId } from '@/lib/helpers/numeric'
import { isSessionOwner } from '@/lib/helpers/session-access'
import {
    clockTime,
    fetchWaitingHeartrateUploadsFor,
    heartrateChoice,
    heartrateUploadLabel,
    heartrateUploadOptions,
    proposedHeartrateUpload,
    sessionInterval,
} from '@/lib/sessions/heartrate-uploads'

import Loading from '@/components/Loading.vue'
import Button from '@/components/buttons/Primary.vue'
import Modal from '@/components/modals/Modal.vue'
import Information from '@/components/Information.vue'
import SessionMetadataFields from '@/components/modals/SessionMetadataFields.vue'
import HeartrateUploadSelect from '@/components/sessions/HeartrateUploadSelect.vue'

// P7, « Vérifiez votre session »: the server proposed the horse, the rider, the
// discipline and the cardio of the session; « Confirmer » confirms them,
// « Modifier » opens the form to change them, saved by « Enregistrer ». Both
// validate the session.
const props = defineProps({
    isOpen: { type: Boolean, default: false },
    session: { type: Object, default: undefined },
})

const emits = defineEmits(['update:isOpen', 'update:session'])

const { t, locale } = useI18n()
const store = useStore()
// The onboarding guide shows its card over this window only for the rider's own
// Move Pro session, the one whose validation moves the guide on.
const advancesTutorial = computed(
    () => props.session?.has_movepro_upload === true && isSessionOwner(props.session, store.state.auth?.user?.id)
)
const toast = useToast()

const setOpen = (state) => {
    emits('update:isOpen', state)
}

const { horses } = useHorses()
const { riders } = useRiders()
const { updateSession } = useSessions()
const { sensors } = useSensors()
const { metas } = useMetas()

// The session as GET /sessions/{id} gives it: the list's copy has neither its
// launch nor its cardio.
const proposal = ref(undefined)
const waitingUploads = ref([])
const loadFailed = ref(false)
const editing = ref(false)
const saving = ref(false)
const form = reactive({ horseId: '', riderId: '', discipline: '', heartrateUploadId: '' })

// Archived horses are no longer offered, except the session's own horse.
const horsesOptions = computed(() =>
    withKeptHorse(activeHorsesOf(horses.value, proposal.value?.horse_id), proposal.value?.horse)
)

const ridersOptions = computed(() => {
    if (
        proposal.value?.rider &&
        riders?.value &&
        !riders.value.find((rider) => rider.id === proposal.value.rider.rider_id)
    ) {
        return [...riders.value, proposal.value.rider]
    }
    return riders?.value
})

const disciplineOptions = computed(() => {
    if (!metas.value?.['session-discipline']) return []
    return Object.keys(metas.value['session-discipline']).map((discipline) => ({
        value: discipline,
        label: t(`session.discipline.${discipline?.toLowerCase()?.replaceAll(' ', '-')}`),
    }))
})

const sessionSummary = computed(() => {
    const date = proposal.value?.start_at
        ? new Date(proposal.value.start_at).toLocaleString(locale.value, {
              weekday: 'short',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : ''
    const duration = Number(proposal.value?.elapsed_time) > 0 ? secondsToHMS(proposal.value.elapsed_time, 3, true) : ''

    return { date, duration }
})

const riderName = computed(() =>
    [proposal.value?.rider?.first_name, proposal.value?.rider?.last_name].filter(Boolean).join(' ')
)
const disciplineLabel = computed(
    () => disciplineOptions.value.find((option) => option.value === proposal.value?.discipline)?.label || ''
)

const around = computed(() => sessionInterval(proposal.value))
const proposedHeartrate = computed(() =>
    proposedHeartrateUpload(proposal.value?.heartrate_upload, waitingUploads.value, around.value)
)
const heartrateOptions = computed(() =>
    heartrateUploadOptions(proposal.value?.heartrate_upload, waitingUploads.value, around.value)
)

// Where the proposal comes from: the launch made in the app, else the horse of
// the session's MOVE PRO sensor, only when it is that horse (the server may
// also have taken the main horse, or none).
const sensorHorseId = computed(
    () => sensors.value?.find?.((sensor) => sensor.id === proposal.value?.sensor_id)?.horse_id ?? null
)
const origin = computed(() => {
    if (proposal.value?.recording?.started_at) {
        return {
            icon: ClockIcon,
            text: t('session.validation.from-launch', {
                time: clockTime(proposal.value.recording.started_at, locale.value),
            }),
        }
    }
    return proposal.value?.horse_id && proposal.value.horse_id === sensorHorseId.value
        ? { icon: SignalIcon, text: t('session.validation.from-sensor') }
        : null
})

// The lines of the card, each named; what was not found reads « Non renseigné ».
// A horse or a rider shows its photo, else its initials; the origin sits on the horse line.
const lines = computed(() => [
    {
        key: 'horse',
        label: t('horse.title'),
        value: proposal.value?.horse?.name || '',
        photo: photoOf(proposal.value?.horse?.avatar),
        initials: initialsOf(proposal.value?.horse?.name),
        origin: origin.value,
    },
    {
        key: 'rider',
        label: t('user.rider'),
        value: riderName.value,
        photo: photoOf(proposal.value?.rider?.avatar),
        initials: initialsOf(riderName.value),
        icon: UserIcon,
    },
    { key: 'discipline', label: t('session.discipline.title'), value: disciplineLabel.value, icon: FlagIcon },
    {
        key: 'cardio',
        label: t('session.validation.cardio'),
        value: proposedHeartrate.value ? heartrateUploadLabel(proposedHeartrate.value, locale.value) : '',
        icon: HeartIcon,
    },
])

const load = async () => {
    const sessionId = props.session?.id
    editing.value = false
    if (!props.isOpen || !sessionId) return

    proposal.value = undefined
    loadFailed.value = false
    try {
        const fresh = await fetcher(`/sessions/${sessionId}`, undefined, { forceNetwork: true })
        // Read once the session is: only its owners are offered their waiting captures.
        const waiting = await fetchWaitingHeartrateUploadsFor(fresh, store.state.auth?.user?.id).catch(() => [])
        if (props.session?.id !== sessionId) return
        if (!fresh?.id) {
            loadFailed.value = true
            return
        }

        proposal.value = fresh
        waitingUploads.value = waiting
        Object.assign(form, {
            horseId: parseNullableId(fresh.horse_id) ?? '',
            riderId: parseNullableId(fresh.rider_id) ?? '',
            discipline: fresh.discipline ?? '',
            heartrateUploadId: proposedHeartrate.value?.id ?? '',
        })
    } catch {
        if (props.session?.id === sessionId) loadFailed.value = true
    }
}

watch([() => props.isOpen, () => props.session?.id], load, { immediate: true })

const save = async (changes) => {
    if (!proposal.value?.id || saving.value) return

    saving.value = true
    try {
        const updatedSession = await updateSession(proposal.value.id, { ...changes, validated: 1 })
        void reloadEvents()
        emits('update:session', updatedSession)
        setOpen(false)
    } catch (error) {
        toast.add({
            severity: 'warn',
            summary: error?.response?.data?.message || error?.message || t('error.failed-to-load'),
            life: 3000,
        })
    } finally {
        saving.value = false
    }
}

// « Confirmer »: one call, with the proposed capture when it is still waiting.
const confirm = () => save(heartrateChoice(proposal.value?.heartrate_upload?.id, proposedHeartrate.value?.id))

// « Modifier » then « Enregistrer »: the horse change is not computed again (P7 decision).
const saveForm = () =>
    save({
        horse_id: parseNullableId(form.horseId),
        rider_id: parseNullableId(form.riderId),
        discipline: form.discipline === '' ? null : form.discipline,
        ...heartrateChoice(proposal.value?.heartrate_upload?.id, form.heartrateUploadId),
    })
</script>
